import { z } from 'zod';

export const LANDIQ_DATA_RUN_ENTRY_STATES = ['PENDING', 'BLOCKED', 'RUNNING', 'DONE', 'FAILED'] as const;
export const LANDIQ_DATA_RUN_STATES = ['RUNNING', 'DONE', 'FAILED'] as const;
export const LANDIQ_DATA_RUN_COMPLETIONS = ['EXIT', 'IMPORT'] as const;
export const LANDIQ_DATA_RUN_ORIGINS = ['API', 'MANUAL'] as const;
export const LANDIQ_DATA_RUN_PHASES = ['COMMAND', 'IMPORT'] as const;
export const LANDIQ_BOARD_COLUMNS = ['PENDING', 'UPLOAD', 'BUILD', 'BACKFILL', 'IMPORTED'] as const;
export const LANDIQ_BOARD_STAGES = ['UPLOAD', 'BUILD', 'BACKFILL'] as const;
export const LANDIQ_BOARD_STAGE_STATES = ['RUNNING', 'FAILED', 'INCOMPLETE', 'DONE'] as const;
/** Why a run failed, when LandIQ knows: SOURCE_OUTAGE — the publisher was down; retry later, not a bug. */
export const LANDIQ_DATA_RUN_FAILURE_REASONS = ['SOURCE_OUTAGE'] as const;
export const LANDIQ_DATA_RUN_REFUSAL_REASONS = [
  'BLOCKED',
  'ALREADY_RUNNING',
  'ALREADY_DONE',
  'ENVIRONMENT_NOT_ALLOWED',
  'RUNNER_UNAVAILABLE',
  'UNKNOWN_ENTRY',
] as const;

export type LandIqDataRunEntryState = (typeof LANDIQ_DATA_RUN_ENTRY_STATES)[number];
export type LandIqDataRunState = (typeof LANDIQ_DATA_RUN_STATES)[number];
export type LandIqDataRunCompletion = (typeof LANDIQ_DATA_RUN_COMPLETIONS)[number];
export type LandIqDataRunOrigin = (typeof LANDIQ_DATA_RUN_ORIGINS)[number];
export type LandIqDataRunPhase = (typeof LANDIQ_DATA_RUN_PHASES)[number];
export type LandIqBoardColumn = (typeof LANDIQ_BOARD_COLUMNS)[number];
export type LandIqBoardStage = (typeof LANDIQ_BOARD_STAGES)[number];
export type LandIqBoardStageState = (typeof LANDIQ_BOARD_STAGE_STATES)[number];
export type LandIqDataRunFailureReason = (typeof LANDIQ_DATA_RUN_FAILURE_REASONS)[number];
export type LandIqDataRunRefusalReason = (typeof LANDIQ_DATA_RUN_REFUSAL_REASONS)[number];

/**
 * The latest progress snapshot a command wrote (LandIQ ADR data-runs/0027): at most ten minutes old while the
 * command makes progress. Only commands that report progress have one.
 */
export const LandIqDataRunCommandProgressSchema = z.object({
  phase: z.string(), // LandIQ's free-text name of the command's current phase
  phaseStartedAt: z.iso.datetime(),
  done: z.number().int().nonnegative(),
  total: z.number().int().nonnegative().nullable(),
  requestsLive: z.number().int().nonnegative(),
  requestsReplayed: z.number().int().nonnegative(),
  retries: z.number().int().nonnegative(),
  itemsPerMinute: z.number().nonnegative().nullable(),
  etaAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
});
export type LandIqDataRunCommandProgress = z.infer<typeof LandIqDataRunCommandProgressSchema>;

/** Time spent per stage, up to now while the run runs. A part LandIQ has no moment for is null. */
export const LandIqDataRunTimingsSchema = z.object({
  queuedMs: z.number().nonnegative().nullable(),
  commandMs: z.number().nonnegative().nullable(),
  importMs: z.number().nonnegative().nullable(),
  totalMs: z.number().nonnegative(),
});
export type LandIqDataRunTimings = z.infer<typeof LandIqDataRunTimingsSchema>;

/** An import run's chunk summary. `rowsWritten` stays null until an importer reports it — never 0. */
export const LandIqDataRunChunkStatsSchema = z.object({
  seen: z.number().int().nonnegative(),
  imported: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  rowsWritten: z.number().int().nonnegative().nullable(),
  p50DurationMs: z.number().nonnegative().nullable(),
  maxDurationMs: z.number().nonnegative().nullable(),
});
export type LandIqDataRunChunkStats = z.infer<typeof LandIqDataRunChunkStatsSchema>;

export const LANDIQ_DATA_RUN_CHUNK_OUTCOMES = ['IMPORTED', 'FAILED'] as const;
export type LandIqDataRunChunkOutcome = (typeof LANDIQ_DATA_RUN_CHUNK_OUTCOMES)[number];

export const LandIqDataRunChunkStatSchema = z.object({
  chunkPath: z.string(),
  outcome: z.enum(LANDIQ_DATA_RUN_CHUNK_OUTCOMES),
  rowsWritten: z.number().int().nonnegative().nullable(),
  durationMs: z.number().nonnegative(),
  attempts: z.number().int().nonnegative(),
  error: z.string().nullable(),
  finishedAt: z.iso.datetime(),
});
export type LandIqDataRunChunkStat = z.infer<typeof LandIqDataRunChunkStatSchema>;

/** What a run says about where its time went. Null when LandIQ has none, e.g. a run recorded before ADR 0027. */
export const LandIqDataRunMetricsSchema = z.object({
  commandProgress: LandIqDataRunCommandProgressSchema.nullable(),
  timings: LandIqDataRunTimingsSchema.nullable(),
  chunkStats: LandIqDataRunChunkStatsSchema.nullable(), // null on trigger, move and retry answers, which do not read it
});
export type LandIqDataRunMetrics = z.infer<typeof LandIqDataRunMetricsSchema>;

/**
 * How a run compares with its manifest entry's estimate (LandIQ pull request 720). `actualMinutes` runs from the command's start
 * to its finish, or to now while it runs, without queue time; a negative overrun is the time left.
 */
export const LandIqDataRunEstimateSchema = z.object({
  estimateMinutes: z.number().nonnegative(),
  actualMinutes: z.number().nonnegative(),
  overrunMinutes: z.number(),
  overrunRatio: z.number(),
  overEstimate: z.boolean(),
});
export type LandIqDataRunEstimate = z.infer<typeof LandIqDataRunEstimateSchema>;

export const LandIqDataRunSchema = z.object({
  id: z.string(),
  entryId: z.string().nullable(),
  command: z.string(),
  args: z.array(z.string()),
  environment: z.string(),
  origin: z.enum(LANDIQ_DATA_RUN_ORIGINS),
  state: z.enum(LANDIQ_DATA_RUN_STATES),
  phase: z.enum(LANDIQ_DATA_RUN_PHASES).nullable(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  exitCode: z.number().int().nullable(),
  error: z.string().nullable(),
  renderJobId: z.string().nullable(),
  failureReason: z.enum(LANDIQ_DATA_RUN_FAILURE_REASONS).nullable(), // null unless the run failed for a known reason
  estimate: LandIqDataRunEstimateSchema.nullable(), // null when LandIQ sends none, e.g. the entry left the manifest
  ...LandIqDataRunMetricsSchema.shape,
});
export type LandIqDataRun = z.infer<typeof LandIqDataRunSchema>;

export const LandIqDataRunEntrySchema = z.object({
  id: z.string(),
  ticket: z.string(),
  command: z.string(),
  args: z.array(z.string()),
  dependsOn: z.array(z.string()),
  completion: z.enum(LANDIQ_DATA_RUN_COMPLETIONS),
  state: z.enum(LANDIQ_DATA_RUN_ENTRY_STATES),
  blockedBy: z.array(z.string()),
  estimateMinutes: z.number().nonnegative().nullable(), // the manifest's estimate; null when LandIQ sends none
  lastRun: LandIqDataRunSchema.nullable(),
});
export type LandIqDataRunEntry = z.infer<typeof LandIqDataRunEntrySchema>;

export const LandIqBoardTicketSchema = z.object({
  ticket: z.string(),
  title: z.string(),
  url: z.string(),
  stages: z.array(z.enum(LANDIQ_BOARD_STAGES)),
  column: z.enum(LANDIQ_BOARD_COLUMNS),
  columnState: z.enum(LANDIQ_BOARD_STAGE_STATES).nullable(),
  failureReason: z.enum(LANDIQ_DATA_RUN_FAILURE_REASONS).nullable(), // why the column FAILED, when LandIQ knows
  next: z.enum(LANDIQ_BOARD_STAGES).nullable(),
  waitsFor: z.array(z.string()),
  openEntryId: z.string(),
  entries: z.array(
    z.object({
      id: z.string(),
      stage: z.enum(LANDIQ_BOARD_STAGES),
      state: z.enum(LANDIQ_DATA_RUN_ENTRY_STATES),
      estimateMinutes: z.number().nonnegative().nullable(), // null when LandIQ sends none
      lastRunEstimate: LandIqDataRunEstimateSchema.nullable(), // null when the entry never ran or LandIQ sends none
      // the latest progress snapshot of the entry's last run; null when it has none or LandIQ sends none
      lastRunProgress: LandIqDataRunCommandProgressSchema.nullable(),
    }),
  ),
});
export type LandIqBoardTicket = z.infer<typeof LandIqBoardTicketSchema>;

export const LandIqBoardSchema = z.object({
  environment: z.string(),
  generatedAt: z.iso.datetime(),
  tickets: z.array(LandIqBoardTicketSchema),
});
export type LandIqBoard = z.infer<typeof LandIqBoardSchema>;

export const ListLandIqDataRunsResponseSchema = LandIqBoardSchema.extend({
  // the metrics of each entry's latest run, by entry id; an entry that never ran is absent. Empty when LandIQ's
  // run list could not be read — the board shows without them.
  runMetrics: z.record(z.string(), LandIqDataRunMetricsSchema),
});
export type ListLandIqDataRunsResponse = z.infer<typeof ListLandIqDataRunsResponseSchema>;

export const LandIqDataRunDetailSchema = z.object({
  entry: LandIqDataRunEntrySchema,
  run: LandIqDataRunSchema.nullable(),
  progress: z
    .object({
      jobStatus: z.string().nullable(),
      chunks: z
        .object({ incoming: z.number().int(), processed: z.number().int(), failed: z.number().int() })
        .nullable(),
      queueDepth: z.number().int(),
    })
    .nullable(),
  canRetryFailedChunks: z.boolean(), // LandIQ's word on whether the failed chunks can be retried now (LIQ-864)
  canResume: z.boolean(), // LandIQ's word on whether the failed upload can resume (LIQ-868)
  log: z.object({
    lines: z.array(z.object({ at: z.iso.datetime(), text: z.string() })),
    nextCursor: z.string().nullable(),
  }),
  // the board ticket this entry belongs to (LandIQ README 2026-10-12)
  ticket: LandIqBoardTicketSchema,
  slowestChunks: z.array(LandIqDataRunChunkStatSchema), // the run's five slowest chunks, slowest first (ADR 0027)
});
export type LandIqDataRunDetail = z.infer<typeof LandIqDataRunDetailSchema>;

export const StartLandIqDataRunResponseSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('STARTED'), run: LandIqDataRunSchema }),
  z.object({
    outcome: z.literal('REFUSED'),
    reason: z.enum(LANDIQ_DATA_RUN_REFUSAL_REASONS),
    blockedBy: z.array(z.string()),
    runId: z.string().nullable(),
  }),
]);
export type StartLandIqDataRunResponse = z.infer<typeof StartLandIqDataRunResponseSchema>;

export const LANDIQ_DATA_RUN_RESUME_REFUSAL_REASONS = [
  ...LANDIQ_DATA_RUN_REFUSAL_REASONS,
  'RESUME_NOT_POSSIBLE',
] as const;
export type LandIqDataRunResumeRefusalReason = (typeof LANDIQ_DATA_RUN_RESUME_REFUSAL_REASONS)[number];

export const ResumeLandIqDataRunResponseSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('STARTED'), run: LandIqDataRunSchema }),
  z.object({
    outcome: z.literal('REFUSED'),
    reason: z.enum(LANDIQ_DATA_RUN_RESUME_REFUSAL_REASONS),
    blockedBy: z.array(z.string()),
    runId: z.string().nullable(),
  }),
]);
export type ResumeLandIqDataRunResponse = z.infer<typeof ResumeLandIqDataRunResponseSchema>;

export const LANDIQ_DATA_RUN_RETRY_REFUSAL_REASONS = [
  'UNKNOWN_ENTRY',
  'ENVIRONMENT_NOT_ALLOWED',
  'NOT_AN_IMPORT',
  'NO_FAILED_CHUNKS',
  'STORAGE_UNAVAILABLE',
] as const;
export type LandIqDataRunRetryRefusalReason = (typeof LANDIQ_DATA_RUN_RETRY_REFUSAL_REASONS)[number];

export const RetryLandIqDataRunFailedChunksResponseSchema = z.discriminatedUnion('outcome', [
  z.object({ outcome: z.literal('RETRIED'), run: LandIqDataRunSchema, movedChunks: z.number().int().nonnegative() }),
  z.object({ outcome: z.literal('REFUSED'), reason: z.enum(LANDIQ_DATA_RUN_RETRY_REFUSAL_REASONS) }),
]);
export type RetryLandIqDataRunFailedChunksResponse = z.infer<typeof RetryLandIqDataRunFailedChunksResponseSchema>;

export const LANDIQ_BOARD_MOVE_REFUSAL_REASONS = [
  'UNKNOWN_TICKET',
  'IMPORTED_NOT_A_TARGET',
  'ENVIRONMENT_NOT_ALLOWED',
  'NOT_NEXT_STAGE',
  'STAGE_NOT_DONE',
  'BLOCKED',
  'RUNNER_UNAVAILABLE',
] as const;
export type LandIqBoardMoveRefusalReason = (typeof LANDIQ_BOARD_MOVE_REFUSAL_REASONS)[number];

/** A run a move started: always one manifest entry's (README 2026-10-12, "each with its entry_id"). */
export const LandIqStartedRunSchema = LandIqDataRunSchema.extend({ entryId: z.string() });
export type LandIqStartedRun = z.infer<typeof LandIqStartedRunSchema>;

export const LandIqBoardTicketPathParamsSchema = z.object({ ticket: z.string().min(1).max(255) });
export const MoveLandIqDataRunTicketBodySchema = z.object({ to: z.enum(LANDIQ_BOARD_COLUMNS) }).strict();
export type MoveLandIqDataRunTicketBody = z.infer<typeof MoveLandIqDataRunTicketBodySchema>;

export const MoveLandIqDataRunTicketResponseSchema = z.discriminatedUnion('outcome', [
  z.object({
    outcome: z.literal('MOVED'),
    ticket: LandIqBoardTicketSchema,
    runs: z.array(LandIqStartedRunSchema),
  }),
  z.object({
    outcome: z.literal('REFUSED'),
    reason: z.enum(LANDIQ_BOARD_MOVE_REFUSAL_REASONS),
    next: z.enum(LANDIQ_BOARD_STAGES).nullable(),
    column: z.enum(LANDIQ_BOARD_COLUMNS).nullable(),
    columnState: z.enum(LANDIQ_BOARD_STAGE_STATES).nullable(),
    blockedBy: z.array(z.string()),
    waitsFor: z.array(z.string()),
    runs: z.array(LandIqStartedRunSchema),
  }),
]);
export type MoveLandIqDataRunTicketResponse = z.infer<typeof MoveLandIqDataRunTicketResponseSchema>;

export const LandIqDataRunPathParamsSchema = z.object({ entryId: z.string().min(1).max(255) });
export type LandIqDataRunPathParams = z.infer<typeof LandIqDataRunPathParamsSchema>;

export const GetLandIqDataRunQuerySchema = z.object({ logAfter: z.string().min(1).max(512).optional() });
export type GetLandIqDataRunQuery = z.infer<typeof GetLandIqDataRunQuerySchema>;
